import { AuthedButton } from '../authed-button';
import { SpanLoading } from '../fetch-status/span-loading';
import { ButtonProps } from '../ui/button';
import { InteractLikedIcon, InteractLikeIcon } from '@/client/assets';
import { IS_DEBUG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { formatNumber } from '@/client/utils/format';
import { ContentType } from '@/libs/sdk/api';
import { ReactNode, useRef, useState } from 'react';
import useHoverDirty from 'react-use/lib/useHoverDirty';

type LikeContextProps = ButtonProps & {
  isLinked: boolean;
  contentUuid?: string;
  contentType: ContentType;
  linkIcon?: ReactNode;
  linkedIcon?: ReactNode;
  count?: number | string;
  onLinkChange?: (isLink: boolean) => void;
};

export const LikeContext = ({
  isLinked,
  contentUuid,
  contentType,
  linkIcon = <InteractLikeIcon className="size-4 md:size-5 fill-foreground/70" />,
  linkedIcon = <InteractLikedIcon className="size-4 md:size-5 fill-foreground" />,
  onLinkChange,
  count,
  ...props
}: LikeContextProps) => {
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const [likeCount, setLikeCount] = useState(Number(count ?? 0));
  const [isLink, setIsLink] = useState(isLinked);
  const ref = useRef<HTMLButtonElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isHover = useHoverDirty(ref);

  const handleLink = async () => {
    if (!contentUuid) return;
    setIsLoading(true);
    try {
      await api.community.likeContent({ baseTokenAddress }, { contentUuid, contentType });
      setIsLink(true);
      onLinkChange?.(true);
      setLikeCount(likeCount ? likeCount + 1 : 1);
    } catch (error) {
      if (IS_DEBUG) console.log('LikeContext error:', error);
      // const msg = getErrorMessage(error);
    }
    setIsLoading(false);
  };
  const handleUnLink = async () => {
    if (!contentUuid) return;
    setIsLoading(true);
    try {
      await api.community.unlikeContent({ baseTokenAddress }, { contentUuid, contentType });
      setIsLink(false);
      onLinkChange?.(false);
      setLikeCount(likeCount ? likeCount - 1 : 0);
    } catch (error) {
      if (IS_DEBUG) console.log('unLikeContext error:', error);
      // const msg = getErrorMessage(error);
    }
    setIsLoading(false);
  };
  return (
    <div className="gap-1 md:gap-2 flex items-center">
      <AuthedButton
        {...props}
        ref={ref}
        type="button"
        aria-label={isLink ? 'Unlike' : 'Like'}
        showAuth={false}
        showLoading={false}
        variant={props.variant ?? 'ghost'}
        className={cn('size-6 rounded-full peer', props.className)}
        disabled={props.disabled || isLoading}
        onClick={e => {
          if (isLink) handleUnLink();
          else handleLink();
          props.onClick?.(e);
        }}
      >
        {isLoading ? (
          <SpanLoading />
        ) : isLink ? (
          <>{isHover ? linkIcon : linkedIcon}</>
        ) : (
          <>{isHover ? linkedIcon : linkIcon}</>
        )}
      </AuthedButton>
      {likeCount && <span>{formatNumber(likeCount)}</span>}
    </div>
  );
};
